import { supabase } from '../supabase';
import { Species } from '../utils/types';

export interface ExternalMarketProfile {
  type: 'market';
  key: string;
  label: string;
  importer: {
    id: number;
    name: string | null;
    logo_url?: string | null;
    excel_name?: string | null;
  };
}

export interface ReceiverRelationship {
  access_id: number;
  exporter_id: number;
  exporter_name: string;
  exporter_logo_url?: string | null;
  exporter_receiver_id: number;
  client_name: string;
  scopes: ReceiverAccessScope[];
}

export interface ReceiverAccessScope {
  id?: number;
  season: string;
  species_id: number;
  species?: Species;
}

export interface ExternalReceiverProfile {
  type: 'receiver';
  key: string;
  label: string;
  relationships: ReceiverRelationship[];
}

export type ExternalProfile = ExternalMarketProfile | ExternalReceiverProfile;

export interface ExternalProfilesPayload {
  profiles: ExternalProfile[];
}

export interface ReceiverShipmentRow {
  id: number;
  shipment_id: number;
  exporter_id: number;
  exporter_name: string;
  exporter_logo_url?: string | null;
  exporter_receiver_id: number;
  client_name: string;
  reference_number?: string | null;
  receiver?: string | null;
  packing_name?: string | null;
  ship_name?: string | null;
  ship_etd?: string | null;
  ship_eta?: string | null;
  quantity?: number | null;
  box_weight?: number | null;
  variety?: string | null;
  labeled_variety?: string | null;
  caliber?: string | null;
  ship_destination_country?: string | null;
  ship_destination_port?: string | null;
  business_type?: string | null;
  sale_order?: string | null;
  original_incoterm?: string | null;
  container_number?: string | null;
  box_type?: string | null;
  status?: 'pending' | 'settled';
}

export interface ReceiverShipmentsPayload {
  rows: ReceiverShipmentRow[];
  status_counts: {
    total_shipments: number;
    settled_shipments: number;
  };
}

export interface ReceiverCommitmentRow {
  id: number;
  exporter_id: number;
  exporter_name: string;
  exporter_logo_url?: string | null;
  exporter_receiver_id: number;
  client_name: string;
  season: string;
  species_id: number;
  week: number;
  /** En cajas equivalentes, convertido con el programa de la exportadora. */
  quantity: number;
  created_at?: string;
  /** Cantidad y unidad tal como las lleva la exportadora en su programa. */
  program_quantity?: number;
  program_unit?: 'kg' | 'ceq' | 'shipment';
  date_basis?: 'eta' | 'etd';
  kg_per_ceq?: number;
}

/** Nota del programa comercial de una exportadora (week null = general). */
export interface ReceiverCommitmentNote {
  id: number;
  exporter_id: number;
  exporter_name: string;
  exporter_receiver_id: number;
  client_name: string;
  week: number | null;
  content: string;
  created_at: string;
}

export interface ReceiverOverviewKpis {
  total_shipments: number;
  total_containers: number;
  total_boxes: number;
  total_kg: number;
  pending_shipments: number;
  settled_shipments: number;
  arriving_next_14_days: number;
  arriving_next_14_days_kg: number;
  total_committed: number;
  total_shipped_equivalent_boxes: number;
  fulfillment_pct?: number | null;
  remaining_boxes: number;
  active_exporters: number;
}

export interface ReceiverOverviewWeeklyPoint {
  week: number;
  label: string;
  committed: number;
  shipped: number;
}

export interface ReceiverOverviewFulfillmentRow {
  exporter_id: number;
  exporter_name: string;
  exporter_logo_url?: string | null;
  exporter_receiver_id: number;
  client_name: string;
  committed: number;
  shipped: number;
  balance: number;
  fulfillment_pct?: number | null;
}

export interface ReceiverOverviewArrivalRow {
  shipment_id: number;
  exporter_id: number;
  exporter_name: string;
  exporter_receiver_id: number;
  client_name: string;
  reference_number?: string | null;
  container_number?: string | null;
  ship_name?: string | null;
  ship_eta?: string | null;
  destination?: string | null;
  total_boxes: number;
  total_kg: number;
  status: 'pending' | 'settled';
}

export interface ReceiverOverviewAlert {
  type:
    | 'missing_eta'
    | 'missing_container'
    | 'no_commitment'
    | 'behind_commitment';
  title: string;
  exporter_name: string;
  client_name: string;
  reference_number?: string | null;
  container_number?: string | null;
  balance?: number | null;
}

export interface ReceiverOverviewPayload {
  kpis: ReceiverOverviewKpis;
  weekly_series: ReceiverOverviewWeeklyPoint[];
  fulfillment: ReceiverOverviewFulfillmentRow[];
  upcoming_arrivals: ReceiverOverviewArrivalRow[];
  alerts: ReceiverOverviewAlert[];
}

export async function getExternalProfiles(): Promise<ExternalProfilesPayload> {
  const { data, error } = await supabase.rpc('get_external_profiles');
  if (error) throw error;
  return {
    profiles: ((data as ExternalProfilesPayload | null)?.profiles || []) as ExternalProfile[],
  };
}

export async function getReceiverShipmentsPage(params: {
  season?: string | null;
  speciesId?: number | null;
  exporterId?: number | null;
  exporterReceiverIds?: number[] | null;
}): Promise<ReceiverShipmentsPayload> {
  const { data, error } = await supabase.rpc('get_receiver_shipments_page', {
    p_season: params.season ?? null,
    p_species_id: params.speciesId ?? null,
    p_exporter_id: params.exporterId ?? null,
    p_exporter_receiver_ids: params.exporterReceiverIds ?? null,
  });

  if (error) throw error;

  const payload = data as ReceiverShipmentsPayload | null;
  return {
    rows: payload?.rows || [],
    status_counts: payload?.status_counts || {
      total_shipments: 0,
      settled_shipments: 0,
    },
  };
}

export async function getReceiverCommitments(params: {
  season?: string | null;
  speciesId?: number | null;
  exporterId?: number | null;
  exporterReceiverIds?: number[] | null;
}): Promise<{ commitments: ReceiverCommitmentRow[]; notes: ReceiverCommitmentNote[] }> {
  const { data, error } = await supabase.rpc('get_receiver_commitments', {
    p_season: params.season ?? null,
    p_species_id: params.speciesId ?? null,
    p_exporter_id: params.exporterId ?? null,
    p_exporter_receiver_ids: params.exporterReceiverIds ?? null,
  });

  if (error) throw error;
  const result = data as {
    commitments?: ReceiverCommitmentRow[];
    notes?: ReceiverCommitmentNote[];
  } | null;
  return { commitments: result?.commitments || [], notes: result?.notes || [] };
}

export async function getReceiverOverview(params: {
  season?: string | null;
  speciesId?: number | null;
  exporterId?: number | null;
  exporterReceiverIds?: number[] | null;
}): Promise<ReceiverOverviewPayload> {
  const { data, error } = await supabase.rpc('get_receiver_overview', {
    p_season: params.season ?? null,
    p_species_id: params.speciesId ?? null,
    p_exporter_id: params.exporterId ?? null,
    p_exporter_receiver_ids: params.exporterReceiverIds ?? null,
  });

  if (error) throw error;

  const payload = data as ReceiverOverviewPayload | null;
  return {
    kpis: payload?.kpis || {
      total_shipments: 0,
      total_containers: 0,
      total_boxes: 0,
      total_kg: 0,
      pending_shipments: 0,
      settled_shipments: 0,
      arriving_next_14_days: 0,
      arriving_next_14_days_kg: 0,
      total_committed: 0,
      total_shipped_equivalent_boxes: 0,
      fulfillment_pct: null,
      remaining_boxes: 0,
      active_exporters: 0,
    },
    weekly_series: payload?.weekly_series || [],
    fulfillment: payload?.fulfillment || [],
    upcoming_arrivals: payload?.upcoming_arrivals || [],
    alerts: payload?.alerts || [],
  };
}
